import { inject } from '@angular/core';
import { HttpRequest, HttpHandlerFn, HttpEvent, HttpResponse, HttpInterceptorFn } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { timeout, tap, catchError, finalize } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';
import { BroadcastService } from './services/broadcast.service';
import { Router } from '@angular/router';

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const localStorageService = inject(LocalStorageService);
  const broadcastService = inject(BroadcastService);
  const router = inject(Router);

  const recaptcha = broadcastService.googleRecaptcha.value;

  broadcastService.emitRequestStart();

  if (broadcastService.clientDetails.value?.apikey) {
    request = request.clone({
      withCredentials: true,
      headers: request.headers.set('apikey', broadcastService.clientDetails.value?.apikey),
    });
  }

  if (localStorageService.getToken() != null) {
    request = request.clone({
      withCredentials: true,
      headers: request.headers.set('Token', localStorageService.getToken()),
    });
  }

  if (broadcastService.authorization.value) {
    request = request.clone({
      withCredentials: true,
      headers: request.headers.set('authorization', broadcastService.authorization.value),
    });
  }

  if (
    broadcastService.clientDetails.value?.enable_recaptcha &&
    (request.url.includes('get-gtv') ||
      request.url.includes('get-estimated-tradevalue') ||
      request.url.includes('get-trader-tradevalue')) &&
    recaptcha
  ) {
    request = request.clone({
      withCredentials: true,
      headers: request.headers.set('google_recaptcha_token', recaptcha),
    });
  }

  //Add lead_source to all get-gtv and get-estimated-tradevalue requests
  if (request.url.includes('/trade-value/')) {
    request = request.clone({
      headers: request.headers.set('lead_source', 'TRADEMAX-UI'),
    });
  }

  //Add session-id to all requests if available. This is is used to track user sessions in the backend
  if (broadcastService.clientDetails.value?.session_id) {
    request = request.clone({
      headers: request.headers.set('session-id', broadcastService.clientDetails.value?.session_id),
    });
  }

  return next(request).pipe(
    timeout(getTimeout(request)),
    tap((event: HttpEvent<any>) => {
      if (event instanceof HttpResponse) {
        // Handle successful response if needed
      }
    }),
    catchError((err: any) => {
      if (err.status === 401 || err.status === 403) {
        //redirect to login page. preserve the current path of the url as a parameter to redirect to after login
        const currentUrl = router.url;
        const redirectUrl = encodeURIComponent(currentUrl);
        router.navigate(['/login'], {
          queryParams: { redirectUrl },
        });
        broadcastService.emitAuthorization(null);
      }
      return throwError(err);
    }),
    finalize(() => {
      broadcastService.emitRequestEnd();
    })
  );
};

function getTimeout(request: HttpRequest<any>): number {
  switch (true) {
    case request.url.includes('/clear-cache'):
      return 250000;
    case request.url.includes('/dealer-billing'):
      return 20000;
    case request.url.includes('/scheduled-job'):
      return 240000;
    case request.url.includes('/leads'):
      return 120000;
    case request.url.includes('/dealer-locator/search'):
      return 20000;
    case request.url.includes('/get-gtv'):
      return 15000;
    case request.url.includes('/get-estimated-tradevalue'):
      return 15000;
    case request.url.includes('/get-trader-tradevalue'):
      return 15000;
    default:
      return 10000;
  }
}
